import { useEffect } from "react";
import { CloseOutlined, WarningOutlined } from "@ant-design/icons";
import { TriggerErrorPayload } from "@dots-projects/utils/exceptions";
import { Button } from "antd";
import { useTriggerEditorStateDispatch } from "./TriggerEditor";
import {
  hideNodeErrorBorder,
  selectNode,
  setStepError,
  setTriggerTypeError,
} from "./reducer/actionCreators";
import { TRIGGER_ROOT_ID } from "./createNodeAndEdges";
import "./ValidationErrorsBox.css";

const DisplayUnsupportedWarning = ({ onOk }: { onOk: () => void }) => (
  <div
    className="absolute bottom-4 left-4 z-20 flex items-center justify-center bg-white flex-col rounded-md border-solid border-2 border-sky-500 py-4 px-5"
    style={{ width: "300px" }}
  >
    <p className="flex items-center justify-center font-semibold text-base">
      <WarningOutlined style={{ marginRight: "10px" }} /> Unsupported
    </p>
    <p className="w-full text-sm">
      Sorry, this step isn't supported on the editor yet. Please reach out to
      support if you want to update this!
    </p>
    <div className="flex gap-2 mt-1 w-full">
      <Button
        onClick={onOk}
        className="modal-button modal-button-text modal-button-link"
      >
        OK!
      </Button>
      <Button className="modal-button modal-button-text modal-button-cancel">
        <a href="https://discord.gg/WJFTPtvGGw" target="_blank">
          Contact support
        </a>
      </Button>
    </div>
  </div>
);

const DisplayErrors = ({
  validationErrors,
}: {
  validationErrors: { message: string }[] | TriggerErrorPayload[];
}) => {
  const { dispatch } = useTriggerEditorStateDispatch();

  const moveViewportToStepError = (stepId: string, rootError: boolean) => {
    dispatch({
      type: "change_viewport",
      payload: { stepId, rootError },
    });
  };

  const openStepErrorModal = (stepId: string, rootError: boolean) => {
    const nodeId = rootError ? TRIGGER_ROOT_ID : stepId;
    dispatch(selectNode(nodeId));
  };

  return (
    <div
      className="absolute bottom-4 left-4 z-20 flex items-center justify-center bg-white flex-col rounded-md border-solid border-2 border-sky-500 py-4 px-5"
      style={{ width: "300px" }}
    >
      <CloseOutlined
        className="absolute top-3 right-3 opacity-60 cursor-pointer"
        onClick={() => {
          dispatch({ type: "hide_errors_box" });
        }}
      />
      <p className="flex items-center justify-center font-semibold text-base mt-1">
        We found some errors 🙁
      </p>
      <p className="w-full text-sm">
        Please fix the error(s) below to test or publish:
      </p>
      <div
        className="w-full error-box-scrollbar text-xs	"
        style={{ maxHeight: "230px", overflow: "scroll" }}
      >
        <ul style={{ listStyleType: "disc", listStylePosition: "inside" }}>
          {validationErrors.map((error) => {
            if ("rootError " in error || "stepId" in error) {
              let triggerError = error as TriggerErrorPayload;
              return (
                <li
                  key={error.message}
                  onClick={() => {
                    moveViewportToStepError(
                      triggerError.stepId!,
                      triggerError.rootError!
                    );
                    openStepErrorModal(
                      triggerError.stepId!,
                      triggerError.rootError!
                    );
                  }}
                >
                  {error.message}{" "}
                  <a href="#">
                    <span className="text-sky-500">
                      Click here to open the error step
                    </span>
                  </a>
                </li>
              );
            }

            return <li key={error.message}>{error.message}</li>;
          })}
        </ul>
      </div>
      <div className="flex gap-2 mt-1 w-full">
        <Button className="modal-button modal-button-text modal-button-cancel">
          <a href="https://discord.gg/WJFTPtvGGw" target="_blank">
            Get help
          </a>
        </Button>
      </div>
    </div>
  );
};

/**
 * the validation error box/modal
 */
export function ValidationErrorsBox({
  validationErrors,
}: {
  validationErrors: { message: string }[] | TriggerErrorPayload[];
}) {
  const placeholderStepPattern = /\$\$step-placeholder-idle-\S{6}/gm;
  validationErrors.forEach((error) => {
    return error.message.replaceAll(placeholderStepPattern, "");
  });

  const { state, dispatch } = useTriggerEditorStateDispatch();
  useEffect(() => {
    // //Clean up errors if exists.

    dispatch(hideNodeErrorBorder());
    // //Set new errors
    validationErrors.forEach((error) => {
      dispatchBorderError(error);
    });
  }, [validationErrors]);

  function dispatchBorderError(errorObject: any) {
    if (errorObject?.rootError) {
      dispatch(setTriggerTypeError());
    } else if (errorObject?.stepId) {
      dispatch(setStepError(errorObject.stepId));
    }
  }

  const onOk = () => {
    dispatch({ type: "unselect" });
    dispatch({ type: "hide_errors_box" });
  };

  switch (state.editor.visibleErrorBoxId) {
    case "unsupported-step":
      return <DisplayUnsupportedWarning onOk={onOk} />;
    case "validation-errors":
      return <DisplayErrors validationErrors={validationErrors} />;
    default:
      return null;
  }
}
